import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Home, 
  BookOpen, 
  Heart, 
  MessageSquare, 
  Calendar, 
  Award, 
  Clock, 
  ChevronRight, 
  Bell, 
  CheckCircle, 
  BookmarkIcon,
  FileText,
  Star,
  Book,
  PenLine,
  MessageCircle,
  Church,
  Trophy,
  BarChart3,
  Sparkles
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UserStats {
  prayerRequests: number;
  testimonials: number;
  bibleBookmarks: number;
  courseProgress: number;
  groupRequests: number;
  ministryRequests: number;
  notifications: number;
  quizResults: number;
  diaryEntries: number;
  spiritualMessages: number;
  totalPoints: number;
}

interface UserProfile {
  name: string;
  email: string;
  createdAt: string;
  lastLogin: string;
  churchName: string;
  level: string;
}

interface RecentActivity {
  id: string;
  type: 'prayer' | 'testimony' | 'bookmark' | 'course' | 'group' | 'ministry' | 'notification' | 'quiz' | 'diary';
  title: string;
  description: string;
  date: string;
  status?: string;
  icon: React.ElementType;
  color: string;
}

interface QuizSummary {
  totalQuizzes: number;
  averageScore: number;
  bestScore: number;
  lastQuizDate: string | null;
}

interface CourseSummary {
  completed: number;
  inProgress: number;
  certificates: number;
  lastActivity: string | null;
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    name: '',
    email: '',
    createdAt: '',
    lastLogin: '',
    churchName: '',
    level: 'Iniciante'
  });
  const [stats, setStats] = useState<UserStats>({
    prayerRequests: 0,
    testimonials: 0,
    bibleBookmarks: 0,
    courseProgress: 0,
    groupRequests: 0,
    ministryRequests: 0,
    notifications: 0,
    quizResults: 0,
    diaryEntries: 0,
    spiritualMessages: 0,
    totalPoints: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [quizSummary, setQuizSummary] = useState<QuizSummary>({
    totalQuizzes: 0,
    averageScore: 0,
    bestScore: 0,
    lastQuizDate: null
  });
  const [courseSummary, setCourseSummary] = useState<CourseSummary>({
    completed: 0,
    inProgress: 0,
    certificates: 0,
    lastActivity: null
  });
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Obter sessão do usuário
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/auth', { state: { returnTo: '/dashboard' } });
          return;
        }
        
        // Obter dados do perfil do usuário
        const userId = session.user.id;
        const userName = session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Usuário';
        const userEmail = session.user.email || '';
        const createdAt = session.user.created_at ? new Date(session.user.created_at).toLocaleDateString('pt-BR') : '';
        const lastLogin = session.user.last_sign_in_at ? formatDistanceToNow(new Date(session.user.last_sign_in_at), { locale: ptBR, addSuffix: true }) : '';
        
        // Obter igreja do usuário
        const { data: userData } = await supabase
          .from('users')
          .select('igreja_id')
          .eq('id', userId)
          .single();
        
        let churchName = 'Igreja não definida';
        if (userData?.igreja_id) {
          const { data: churchData } = await supabase
            .from('igrejas')
            .select('nome')
            .eq('id', userData.igreja_id)
            .single();
          
          if (churchData) {
            churchName = churchData.nome;
          }
        }
        
        setUserProfile({
          name: userName,
          email: userEmail,
          createdAt,
          lastLogin,
          churchName,
          level: 'Discípulo em Crescimento' // Nível padrão, pode ser calculado com base em pontos
        });
        
        // Buscar estatísticas do usuário
        await Promise.all([
          fetchUserStats(userId),
          fetchRecentActivities(userId),
          fetchQuizSummary(userId),
          fetchCourseSummary(userId),
          fetchUnreadNotifications()
        ]);
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);

  const fetchUserStats = async (userId: string) => {
    try {
      // Buscar contagem de pedidos de oração
      const { count: prayerCount } = await supabase
        .from('prayer_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar contagem de testemunhos
      const { count: testimonialsCount } = await supabase
        .from('testimonials')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar contagem de marcadores bíblicos
      const { count: bookmarksCount } = await supabase
        .from('bible_bookmarks')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar contagem de solicitações de grupo
      const { count: groupRequestsCount } = await supabase
        .from('group_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar contagem de solicitações de ministério
      const { count: ministryRequestsCount } = await supabase
        .from('ministry_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar progresso em cursos
      const { count: courseProgressCount } = await supabase
        .from('course_progress')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar resultados de quiz
      const { count: quizResultsCount } = await supabase
        .from('quiz_results')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      // Buscar contagem de entradas no diário (simulado)
      const diaryEntriesCount = Math.floor(Math.random() * 10); // Simulado
      
      // Buscar contagem de mensagens espirituais (simulado)
      const spiritualMessagesCount = Math.floor(Math.random() * 5); // Simulado
      
      // Calcular pontos totais (simulado)
      const totalPoints = (prayerCount || 0) * 5 + 
                         (testimonialsCount || 0) * 10 + 
                         (bookmarksCount || 0) * 2 + 
                         (courseProgressCount || 0) * 20 + 
                         (quizResultsCount || 0) * 15 + 
                         diaryEntriesCount * 5 + 
                         spiritualMessagesCount * 8;
      
      setStats({
        prayerRequests: prayerCount || 0,
        testimonials: testimonialsCount || 0,
        bibleBookmarks: bookmarksCount || 0,
        courseProgress: courseProgressCount || 0,
        groupRequests: groupRequestsCount || 0,
        ministryRequests: ministryRequestsCount || 0,
        notifications: 0, // Será atualizado em fetchUnreadNotifications
        quizResults: quizResultsCount || 0,
        diaryEntries: diaryEntriesCount,
        spiritualMessages: spiritualMessagesCount,
        totalPoints
      });
    } catch (error) {
      console.error('Erro ao buscar estatísticas do usuário:', error);
    }
  };

  const fetchRecentActivities = async (userId: string) => {
    try {
      const activities: RecentActivity[] = [];
      
      // Buscar pedidos de oração recentes
      const { data: prayers } = await supabase
        .from('prayer_requests')
        .select('id, name, request, created_at, is_public')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (prayers) {
        prayers.forEach(prayer => {
          activities.push({
            id: prayer.id,
            type: 'prayer',
            title: 'Pedido de Oração',
            description: prayer.request.substring(0, 50) + (prayer.request.length > 50 ? '...' : ''),
            date: prayer.created_at,
            status: prayer.is_public ? 'Público' : 'Privado',
            icon: Heart,
            color: 'bg-red-100 text-red-600'
          });
        });
      }
      
      // Buscar testemunhos recentes
      const { data: testimonials } = await supabase
        .from('testimonials')
        .select('id, title, content, created_at, status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (testimonials) {
        testimonials.forEach(testimony => {
          activities.push({
            id: testimony.id,
            type: 'testimony',
            title: 'Testemunho',
            description: testimony.title,
            date: testimony.created_at,
            status: testimony.status === 'aprovado' ? 'Aprovado' : testimony.status === 'pendente' ? 'Pendente' : 'Rejeitado',
            icon: MessageSquare,
            color: 'bg-purple-100 text-purple-600'
          });
        });
      }
      
      // Buscar marcadores bíblicos recentes
      const { data: bookmarks } = await supabase
        .from('bible_bookmarks')
        .select('id, book_id, chapter, verse, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (bookmarks) {
        bookmarks.forEach(bookmark => {
          activities.push({
            id: bookmark.id,
            type: 'bookmark',
            title: 'Marcador Bíblico',
            description: `${bookmark.book_id} ${bookmark.chapter}${bookmark.verse ? `:${bookmark.verse}` : ''}`,
            date: bookmark.created_at,
            icon: BookmarkIcon,
            color: 'bg-blue-100 text-blue-600'
          });
        });
      }
      
      // Buscar resultados de quiz recentes
      const { data: quizResults } = await supabase
        .from('quiz_results')
        .select('id, score, total_questions, completed_at')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false })
        .limit(3);
      
      if (quizResults) {
        quizResults.forEach(result => {
          activities.push({
            id: result.id,
            type: 'quiz',
            title: 'Quiz Bíblico',
            description: `Pontuação: ${result.score} de ${result.total_questions * 10}`,
            date: result.completed_at,
            icon: Award,
            color: 'bg-amber-100 text-amber-600'
          });
        });
      }
      
      // Ordenar atividades por data (mais recentes primeiro)
      activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      setRecentActivities(activities.slice(0, 5));
    } catch (error) {
      console.error('Erro ao buscar atividades recentes:', error);
    }
  };

  const fetchQuizSummary = async (userId: string) => {
    try {
      // Buscar resultados de quiz
      const { data: quizResults } = await supabase
        .from('quiz_results')
        .select('score, total_questions, completed_at')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });
      
      if (quizResults && quizResults.length > 0) {
        // Calcular estatísticas
        const totalQuizzes = quizResults.length;
        
        // Calcular pontuação média (considerando que cada questão vale 10 pontos)
        const totalScore = quizResults.reduce((sum, result) => sum + result.score, 0);
        const totalPossibleScore = quizResults.reduce((sum, result) => sum + (result.total_questions * 10), 0);
        const averageScore = Math.round((totalScore / totalPossibleScore) * 100);
        
        // Encontrar melhor pontuação
        const bestScoreResult = quizResults.reduce((best, current) => {
          const currentPercentage = (current.score / (current.total_questions * 10)) * 100;
          const bestPercentage = (best.score / (best.total_questions * 10)) * 100;
          return currentPercentage > bestPercentage ? current : best;
        }, quizResults[0]);
        
        const bestScore = Math.round((bestScoreResult.score / (bestScoreResult.total_questions * 10)) * 100);
        
        // Data do último quiz
        const lastQuizDate = quizResults[0].completed_at;
        
        setQuizSummary({
          totalQuizzes,
          averageScore,
          bestScore,
          lastQuizDate
        });
      }
    } catch (error) {
      console.error('Erro ao buscar resumo de quiz:', error);
    }
  };

  const fetchCourseSummary = async (userId: string) => {
    try {
      // Buscar progresso em cursos
      const { data: courseProgress } = await supabase
        .from('course_progress')
        .select('course_id, is_completed, updated_at')
        .eq('user_id', userId);
      
      if (courseProgress) {
        // Contar cursos completos e em progresso
        const completed = courseProgress.filter(course => course.is_completed).length;
        const inProgress = courseProgress.length - completed;
        
        // Buscar certificados
        const { data: certificates } = await supabase
          .from('course_certificates')
          .select('id')
          .eq('user_id', userId);
        
        // Encontrar data da última atividade
        let lastActivity = null;
        if (courseProgress.length > 0) {
          const sortedProgress = [...courseProgress].sort((a, b) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          );
          lastActivity = sortedProgress[0].updated_at;
        }
        
        setCourseSummary({
          completed,
          inProgress,
          certificates: certificates?.length || 0,
          lastActivity
        });
      }
    } catch (error) {
      console.error('Erro ao buscar resumo de cursos:', error);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      // Buscar contagem de notificações não lidas
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('is_read', false);
      
      setUnreadNotifications(count || 0);
      
      // Atualizar estatísticas
      setStats(prev => ({
        ...prev,
        notifications: count || 0
      }));
    } catch (error) {
      console.error('Erro ao buscar notificações não lidas:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aprovado':
        return 'bg-green-100 text-green-600';
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-600';
      case 'Rejeitado':
        return 'bg-red-100 text-red-600';
      case 'Público':
        return 'bg-green-100 text-green-600';
      case 'Privado':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Determinar o nível do usuário com base nos pontos
  const getUserLevel = (points: number): string => {
    if (points >= 500) return 'Discípulo Avançado';
    if (points >= 300) return 'Discípulo Dedicado';
    if (points >= 150) return 'Discípulo em Crescimento';
    if (points >= 50) return 'Discípulo Iniciante';
    return 'Iniciante na Fé';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <User className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Minha Jornada de Fé</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      {/* Resumo Espiritual */}
      <div className="bg-gradient-to-r from-brand-teal to-brand-teal/80 text-white p-6 rounded-xl shadow-elevation-2 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-semibold mb-2">
              Olá, {userProfile.name}! 👋
            </h2>
            <p className="text-white/90">
              Esta é sua jornada de fé. Continue crescendo e buscando a Deus!
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end">
            <div className="flex items-center gap-2 mb-1">
              <Church className="h-4 w-4" />
              <span>{userProfile.churchName}</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4" />
              <span>Membro desde {userProfile.createdAt}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Último acesso: {userProfile.lastLogin}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pontuação e Gamificação */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              <span>Pontuação e Nível</span>
            </h2>
            <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
              {getUserLevel(stats.totalPoints)}
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center mb-4 md:mb-0">
              <div className="text-4xl font-bold text-brand-teal">{stats.totalPoints}</div>
              <p className="text-gray-600">Pontos Totais</p>
            </div>
            
            <div className="w-full md:w-3/5">
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-brand-teal bg-brand-light">
                      Progresso para próximo nível
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-brand-teal">
                      {stats.totalPoints >= 500 ? '100%' : 
                       stats.totalPoints >= 300 ? `${Math.min(100, Math.round((stats.totalPoints - 300) / 2))}%` :
                       stats.totalPoints >= 150 ? `${Math.min(100, Math.round((stats.totalPoints - 150) / 1.5))}%` :
                       stats.totalPoints >= 50 ? `${Math.min(100, Math.round((stats.totalPoints - 50)))}%` :
                       `${Math.min(100, Math.round(stats.totalPoints * 2))}%`}
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-brand-light/30">
                  <div 
                    style={{ 
                      width: stats.totalPoints >= 500 ? '100%' : 
                             stats.totalPoints >= 300 ? `${Math.min(100, Math.round((stats.totalPoints - 300) / 2))}%` :
                             stats.totalPoints >= 150 ? `${Math.min(100, Math.round((stats.totalPoints - 150) / 1.5))}%` :
                             stats.totalPoints >= 50 ? `${Math.min(100, Math.round((stats.totalPoints - 50)))}%` :
                             `${Math.min(100, Math.round(stats.totalPoints * 2))}%` 
                    }} 
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-brand-teal"
                  ></div>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                {stats.totalPoints >= 500 ? 'Você atingiu o nível máximo! Parabéns!' : 
                 stats.totalPoints >= 300 ? `Faltam ${500 - stats.totalPoints} pontos para "Discípulo Avançado"` :
                 stats.totalPoints >= 150 ? `Faltam ${300 - stats.totalPoints} pontos para "Discípulo Dedicado"` :
                 stats.totalPoints >= 50 ? `Faltam ${150 - stats.totalPoints} pontos para "Discípulo em Crescimento"` :
                 `Faltam ${50 - stats.totalPoints} pontos para "Discípulo Iniciante"`}
              </div>
            </div>
          </div>
          
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-blue-600 font-semibold">{stats.bibleBookmarks}</div>
              <div className="text-xs text-gray-600">Marcadores</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-purple-600 font-semibold">{stats.testimonials}</div>
              <div className="text-xs text-gray-600">Testemunhos</div>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg">
              <div className="text-amber-600 font-semibold">{stats.quizResults}</div>
              <div className="text-xs text-gray-600">Quizzes</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-green-600 font-semibold">{stats.courseProgress}</div>
              <div className="text-xs text-gray-600">Cursos</div>
            </div>
          </div>
        </div>

        {/* Histórico de Quiz Bíblico */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              <span>Quiz Bíblico</span>
            </h2>
            <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
              {quizSummary.totalQuizzes} {quizSummary.totalQuizzes === 1 ? 'quiz' : 'quizzes'}
            </div>
          </div>
          
          {quizSummary.totalQuizzes > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-amber-50 p-3 rounded-lg">
                  <div className="text-amber-600 font-semibold">{quizSummary.averageScore}%</div>
                  <div className="text-xs text-gray-600">Média</div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-green-600 font-semibold">{quizSummary.bestScore}%</div>
                  <div className="text-xs text-gray-600">Melhor</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-blue-600 font-semibold">{quizSummary.totalQuizzes}</div>
                  <div className="text-xs text-gray-600">Total</div>
                </div>
              </div>
              
              {quizSummary.lastQuizDate && (
                <div className="text-sm text-gray-600 text-center">
                  Último quiz: {formatDate(quizSummary.lastQuizDate)}
                </div>
              )}
              
              <button
                onClick={() => navigate('/bible-quiz')}
                className="w-full mt-4 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
              >
                <Award className="h-4 w-4" />
                <span>Jogar Novamente</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Você ainda não participou de nenhum quiz bíblico.</p>
              <button
                onClick={() => navigate('/bible-quiz')}
                className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors"
              >
                Começar Primeiro Quiz
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cursos Bíblicos e Pedidos de Oração */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Cursos Bíblicos */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <span>Cursos Bíblicos</span>
            </h2>
            <button
              onClick={() => navigate('/cursos')}
              className="text-sm text-brand-teal hover:underline flex items-center"
            >
              Ver todos <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {courseSummary.completed > 0 || courseSummary.inProgress > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-green-600 font-semibold">{courseSummary.completed}</div>
                  <div className="text-xs text-gray-600">Concluídos</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-blue-600 font-semibold">{courseSummary.inProgress}</div>
                  <div className="text-xs text-gray-600">Em Progresso</div>
                </div>
              </div>
              
              {courseSummary.certificates > 0 && (
                <div className="bg-amber-50 p-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-600" />
                    <div>
                      <div className="font-medium">{courseSummary.certificates} Certificado{courseSummary.certificates !== 1 ? 's' : ''}</div>
                      <div className="text-xs text-gray-600">Emitido{courseSummary.certificates !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/cursos/certificado')}
                    className="text-amber-600 hover:text-amber-700"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
              
              {courseSummary.lastActivity && (
                <div className="text-sm text-gray-600 text-center">
                  Última atividade: {formatDate(courseSummary.lastActivity)}
                </div>
              )}
              
              <button
                onClick={() => navigate('/cursos')}
                className="w-full mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <BookOpen className="h-4 w-4" />
                <span>Continuar Aprendendo</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Você ainda não iniciou nenhum curso bíblico.</p>
              <button
                onClick={() => navigate('/cursos')}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Explorar Cursos
              </button>
            </div>
          )}
        </div>

        {/* Pedidos de Oração */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-500" />
              <span>Pedidos de Oração</span>
            </h2>
            <button
              onClick={() => navigate('/prayer')}
              className="text-sm text-brand-teal hover:underline flex items-center"
            >
              Ver todos <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {stats.prayerRequests > 0 ? (
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-red-500" />
                    <div>
                      <div className="font-medium">{stats.prayerRequests} Pedido{stats.prayerRequests !== 1 ? 's' : ''} Realizado{stats.prayerRequests !== 1 ? 's' : ''}</div>
                      <div className="text-xs text-gray-600">Nossa equipe está orando por você</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {recentActivities.filter(activity => activity.type === 'prayer').length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Pedidos Recentes</h3>
                  <div className="space-y-2">
                    {recentActivities
                      .filter(activity => activity.type === 'prayer')
                      .map(activity => (
                        <div key={activity.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <p className="text-sm text-gray-700 line-clamp-1">{activity.description}</p>
                            {activity.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(activity.status)}`}>
                                {activity.status}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(activity.date)}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              <button
                onClick={() => navigate('/prayer')}
                className="w-full mt-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
              >
                <Heart className="h-4 w-4" />
                <span>Novo Pedido</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Você ainda não fez nenhum pedido de oração.</p>
              <button
                onClick={() => navigate('/prayer')}
                className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Fazer Primeiro Pedido
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Testemunhos e Meu Diário com Deus */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Testemunhos */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              <span>Testemunhos</span>
            </h2>
            <button
              onClick={() => navigate('/testimonials')}
              className="text-sm text-brand-teal hover:underline flex items-center"
            >
              Ver todos <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          
          {stats.testimonials > 0 ? (
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                    <div>
                      <div className="font-medium">{stats.testimonials} Testemunho{stats.testimonials !== 1 ? 's' : ''} Compartilhado{stats.testimonials !== 1 ? 's' : ''}</div>
                      <div className="text-xs text-gray-600">Sua história inspira outros</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {recentActivities.filter(activity => activity.type === 'testimony').length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Testemunhos Recentes</h3>
                  <div className="space-y-2">
                    {recentActivities
                      .filter(activity => activity.type === 'testimony')
                      .map(activity => (
                        <div key={activity.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <p className="text-sm text-gray-700 font-medium">{activity.description}</p>
                            {activity.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(activity.status)}`}>
                                {activity.status}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(activity.date)}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              <button
                onClick={() => navigate('/testimonials')}
                className="w-full mt-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                <span>Compartilhar Testemunho</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Você ainda não compartilhou nenhum testemunho.</p>
              <button
                onClick={() => navigate('/testimonials')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Compartilhar Testemunho
              </button>
            </div>
          )}
        </div>

        {/* Meu Diário com Deus */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <PenLine className="h-5 w-5 text-green-600" />
              <span>Meu Diário com Deus</span>
            </h2>
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
              {stats.diaryEntries} {stats.diaryEntries === 1 ? 'registro' : 'registros'}
            </div>
          </div>
          
          {stats.diaryEntries > 0 ? (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <PenLine className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium">Seu diário espiritual</div>
                    <div className="text-xs text-gray-600">Registre suas experiências com Deus</div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Seu diário espiritual é um espaço privado para registrar suas experiências, orações respondidas e momentos especiais com Deus.
                </p>
                <button
                  onClick={() => navigate('/spiritual-counselor')}
                  className="w-full mt-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <PenLine className="h-4 w-4" />
                  <span>Novo Registro</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <PenLine className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                Comece seu diário espiritual hoje! Registre suas experiências, orações respondidas e momentos especiais com Deus.
              </p>
              <button
                onClick={() => navigate('/spiritual-counselor')}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Criar Primeiro Registro
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Deus, Preciso Falar... e Atividades Recentes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Deus, Preciso Falar... */}
        <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-indigo-600" />
              <span>Deus, Preciso Falar...</span>
            </h2>
            <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
              Confidencial
            </div>
          </div>
          
          {stats.spiritualMessages > 0 ? (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-indigo-600" />
                  <div>
                    <div className="font-medium">{stats.spiritualMessages} Mensagem{stats.spiritualMessages !== 1 ? 'ns' : ''}</div>
                    <div className="text-xs text-gray-600">Suas conversas com Deus</div>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Este é um espaço seguro e confidencial para você expressar seus pensamentos, dúvidas e orações diretamente a Deus.
                </p>
                <button
                  onClick={() => navigate('/spiritual-guidance')}
                  className="w-full mt-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Nova Mensagem</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                Este é um espaço seguro e confidencial para você expressar seus pensamentos, dúvidas e orações diretamente a Deus.
              </p>
              <button
                onClick={() => navigate('/spiritual-guidance')}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Iniciar Conversa
              </button>
            </div>
          )}
        </div>

        {/* Atividades Recentes */}
        <div className="lg:col-span-2">
          <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-brand-teal" />
                <span>Atividades Recentes</span>
              </h2>
            </div>
            
            {recentActivities.length > 0 ? (
              <div className="space-y-3">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${activity.color} shrink-0`}>
                        <activity.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-900">{activity.title}</h3>
                            <p className="text-sm text-gray-600 mt-0.5">{activity.description}</p>
                          </div>
                          {activity.status && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(activity.status)}`}>
                              {activity.status}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(activity.date)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma atividade recente encontrada</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-brand-teal" />
        <span>Ações Rápidas</span>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <button 
          onClick={() => navigate('/prayer')}
          className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow text-left flex items-start gap-4"
        >
          <div className="p-3 rounded-xl bg-red-100 text-red-600">
            <Heart className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Novo Pedido de Oração</h3>
            <p className="text-sm text-gray-600 mt-1">Compartilhe suas necessidades</p>
          </div>
        </button>
        
        <button 
          onClick={() => navigate('/testimonials')}
          className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow text-left flex items-start gap-4"
        >
          <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
            <MessageSquare className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Compartilhar Testemunho</h3>
            <p className="text-sm text-gray-600 mt-1">Conte o que Deus fez</p>
          </div>
        </button>
        
        <button 
          onClick={() => navigate('/bible')}
          className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow text-left flex items-start gap-4"
        >
          <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
            <Book className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Ler a Bíblia</h3>
            <p className="text-sm text-gray-600 mt-1">Estude a Palavra</p>
          </div>
        </button>
        
        <button 
          onClick={() => navigate('/spiritual-guidance')}
          className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow text-left flex items-start gap-4"
        >
          <div className="p-3 rounded-xl bg-indigo-100 text-indigo-600">
            <MessageCircle className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Orientação Espiritual</h3>
            <p className="text-sm text-gray-600 mt-1">Solicite aconselhamento</p>
          </div>
        </button>
      </div>

      {/* Certificados e Conquistas */}
      <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            <span>Conquistas e Certificados</span>
          </h2>
        </div>
        
        {courseSummary.certificates > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-amber-50 p-4 rounded-lg flex items-center gap-3">
              <Award className="h-8 w-8 text-amber-500" />
              <div>
                <div className="font-medium">{courseSummary.certificates} Certificado{courseSummary.certificates !== 1 ? 's' : ''}</div>
                <div className="text-xs text-gray-600">Cursos concluídos</div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg flex items-center gap-3">
              <Trophy className="h-8 w-8 text-green-500" />
              <div>
                <div className="font-medium">{getUserLevel(stats.totalPoints)}</div>
                <div className="text-xs text-gray-600">Nível atual</div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg flex items-center gap-3">
              <Star className="h-8 w-8 text-blue-500" />
              <div>
                <div className="font-medium">{stats.totalPoints} Pontos</div>
                <div className="text-xs text-gray-600">Pontuação total</div>
              </div>
            </div>
            
            <div className="md:col-span-3 mt-4">
              <button 
                onClick={() => navigate('/cursos/certificado')}
                className="w-full bg-amber-500 text-white px-4 py-3 rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
              >
                <Award className="h-5 w-5" />
                <span>Ver Meus Certificados</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 mb-2">Conquistas Desbloqueadas</h3>
            <p className="text-gray-500 mb-4">Complete cursos e atividades para desbloquear conquistas e certificados.</p>
            <button 
              onClick={() => navigate('/cursos')}
              className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors"
            >
              Explorar Cursos
            </button>
          </div>
        )}
      </div>
    </div>
  );
}